import { inject, observer } from "mobx-react";
import * as React from "react";
import {
  Button,
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "randmarcomps";
import { useManufacturer } from "../../contexts/WithManufacturer";
import { Product } from "../../stores/product/Product";
import { RootStore } from "../../stores/rootStore";
import InlineSpinner from "../util/InlineSpinner";

interface IProps {
  store?: RootStore;
  product: Product;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const EditState: React.FC<IProps> = (props) => {
  const { product, store, open, onOpenChange } = props;
  const { applicationId } = useManufacturer();
  const [loading, setLoading] = React.useState(false);
  const [newState, setNewState] = React.useState(product.state || "");

  React.useEffect(() => {
    if (!open) return;
    setNewState(product.state || "");
  }, [open, product.state]);

  const handleCancel = () => {
    if (loading) return;
    onOpenChange(false);
  };

  const handleConfirmRequest = async () => {
    setLoading(true);
    const { snackStore, productStore } = store!;

    const success = await productStore.setProductProperty(
      applicationId,
      product.randmarSKU || "",
      "State",
      newState?.toString()
    );
    if (success) {
      snackStore.showResult(success);
    }
    setLoading(false);
    onOpenChange(false);

    await productStore.getProduct(applicationId, product.randmarSKU || "");
  };

  const propertyChange = (newValue: string) => {
    setNewState(newValue);
  };

  return (
    <Dialog
      modal
      open={open}
      onOpenChange={(nextOpen) => {
        if (loading) return;
        onOpenChange(nextOpen);
      }}
    >
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Edit product state</DialogTitle>
        </DialogHeader>

        <div className="space-y-3">
          <Select value={newState} onValueChange={propertyChange}>
            <SelectTrigger className="h-11 rounded-xl" disabled={loading}>
              <SelectValue placeholder="Select state" />
            </SelectTrigger>
            <SelectContent className="z-[10002]">
              <SelectItem value="Active">Active</SelectItem>
              <SelectItem value="Discontinued">Discontinued</SelectItem>
              <SelectItem value="Hold">Hold</SelectItem>
            </SelectContent>
          </Select>

          {loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground" role="status" aria-live="polite">
              <InlineSpinner size="sm" decorative />
              Saving…
            </div>
          ) : null}
        </div>

        <DialogFooter className="flex-row justify-between gap-2 sm:justify-between">
          <Button type="button" variant="outline" onClick={handleCancel} disabled={loading}>
            Cancel
          </Button>
          <Button
            type="button"
            onClick={handleConfirmRequest}
            disabled={newState === product.state || loading || newState === ""}
          >
            Confirm
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default inject("store")(observer(EditState));
